import { useCallback, useEffect, useRef } from "react";
import { createWebSpeechRecognitionAdapter } from "./WebSpeechRecognitionAdapter";
import { ashleyLog, ashleyWarn } from "../../debug/ashleySttDebug";
import { msSinceLastGesture, voiceDiag } from "../../debug/ashleyVoiceDiagnostics";

const DEFAULT_SILENCE_TIMEOUT_MS = 8000;

/**
 * Ashley-specific listening lifecycle with silence timeout.
 */
export function useAshleyListening({ onFinalTranscript, onError, onEnd }) {
  const adapterRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const hasResultRef = useRef(false);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const stopListening = useCallback(() => {
    clearSilenceTimer();
    adapterRef.current?.stop();
  }, [clearSilenceTimer]);

  const abortListening = useCallback(() => {
    clearSilenceTimer();
    adapterRef.current?.abort();
  }, [clearSilenceTimer]);

  const startListening = useCallback(() => {
    ashleyLog("useAshleyListening.startListening() invoked");
    const msSinceGesture = msSinceLastGesture();
    voiceDiag("CHAIN 6/8: startListening()", {
      hypothesisId: "H-CHAIN",
      location: "useAshleyListening.startListening",
      data: { msSinceGesture },
    });
    hasResultRef.current = false;
    clearSilenceTimer();

    if (!adapterRef.current) {
      adapterRef.current = createWebSpeechRecognitionAdapter();
    }

    const adapter = adapterRef.current;

    if (!adapter.isSupported()) {
      ashleyWarn("useAshleyListening — STT not supported");
      voiceDiag("startListening aborted — STT unsupported", {
        hypothesisId: "H-E",
        location: "useAshleyListening.startListening",
      });
      onError?.("unsupported");
      onEnd?.();
      return false;
    }

    adapter.onResult((transcript) => {
      ashleyLog("useAshleyListening — final transcript received", { transcript });
      hasResultRef.current = true;
      clearSilenceTimer();
      onFinalTranscript?.(transcript);
      adapter.stop();
    });

    adapter.onError((code) => {
      ashleyWarn("useAshleyListening — adapter error", { code });
      clearSilenceTimer();
      if (code === "no-speech" && !hasResultRef.current) {
        onError?.("no-speech");
        return;
      }
      if (code !== "no-speech") {
        onError?.(code);
      }
    });

    adapter.onEnd(() => {
      ashleyLog("useAshleyListening — adapter onend");
      clearSilenceTimer();
      onEnd?.();
    });

    adapter.start({
      continuous: false,
      interimResults: false,
      lang: "en-US",
    });

    silenceTimerRef.current = setTimeout(() => {
      if (!hasResultRef.current) {
        adapter.stop();
      }
    }, DEFAULT_SILENCE_TIMEOUT_MS);

    return true;
  }, [clearSilenceTimer, onEnd, onError, onFinalTranscript]);

  useEffect(
    () => () => {
      clearSilenceTimer();
      adapterRef.current?.destroy();
      adapterRef.current = null;
    },
    [clearSilenceTimer],
  );

  return {
    startListening,
    stopListening,
    abortListening,
    isSupported: () =>
      adapterRef.current?.isSupported() ??
      createWebSpeechRecognitionAdapter().isSupported(),
  };
}
